import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { NavigateBefore as NavigateBeforeIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import { classes } from '../../Job/Applications/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API from '../API';
import { ApplicationAction, ApplicationState, IApplication } from '../types';
import { checkMissingPlaceholders, INVITE_TO_TIMESLOT_PLACEHOLDER, ModalType } from '../config';
import StyledModal from '../../Components/GenericModal/StyledModal';
import { styles } from './styles';
import { useGetEmailTemplate, useGetSmsTemplatePlaceholders } from '../helper';
import {
  IEventType,
  IRecruiter,
  ScoutCalendarAction,
  ScoutCalendarState
} from '../../../ScoutCalendar/types';
import { IEvent } from '../../Job/types';
import { scrollToElement } from '../../utils/scroll-to-element';
import { MissingPlaceholdersConfirmationModal } from './MissingPlaceholdersConfirmationModal';
import { ISMSTemplate } from '../../Job/types';
import { useDispatch } from '../../hooks/useDispatch';
import formatToUserTimeZone from '../../utils/formatToUserTimeZone';
import { IJob } from '../../EditJob/types';
import InviteToTimeslotStep1 from '../../Job/Applications/Modals/InviteToTimeslotStep1';
import InviteToTimeslotStep2 from '../../Job/Applications/Modals/InviteToTimeslotStep2';
import NoTimeslotsDialog from '../../Job/Applications/Modals/NoTimeslotsDialog';
import { useEmailTemplatesOptions } from '../../hooks';
import CreateEventButton from '../../Components/Buttons/CreateEventButton';

interface InviteToTimeslotProps {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
  smsTemplatePermissions: boolean;
  jobId: number;
  calendarDispatch: Dispatch<ScoutCalendarAction>;
  ScoutCalendarState: ScoutCalendarState;
}

interface SMSResponse {
  success: number;
  failure: number;
  message: string;
}

export default function NewInviteToTimeslot({
  ApplicationState,
  dispatch,
  smsTemplatePermissions,
  jobId,
  calendarDispatch,
  ScoutCalendarState
}: InviteToTimeslotProps) {
  const [selectedEventType, setSelectedEventType] = useState<IEventType | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [step, setStep] = useState<number>(1);
  const [notification, setNotification] = useState<string>('email');
  const [smsMessage, setSmsMessage] = useState<string>('');
  const [smsTemplate, setSmsTemplate] = useState<ISMSTemplate | null>(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missingPlaceholderConfirmation, setMissingPlaceholderConfirmation] =
    useState<boolean>(false);
  const [filterType, setFilterType] = useState<'owner' | 'job' | 'recruiter'>('owner');
  const [selectedRecruiter, setSelectedRecruiter] = useState<IRecruiter | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [spots, setSpots] = useState(0);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [allEvents, setAllEvents] = useState<IEvent[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    formatToUserTimeZone(new Date(), 'ymd')
  );
  const [timeslotHovered, setTimeslotHovered] = useState<number | null>(null);
  const [excludeApplicants, setExcludeApplicants] = useState(false);
  const [isLoading, setIsLoading] = useState({ events: false, eventTypes: false });
  const [noTimeslots, setNoTimeslots] = useState<boolean>(false);
  const [notEnoughSpots, setNotEnoughSpots] = useState<boolean>(false);
  const [showNoTimeslotsModal, setShowNoTimeslotsModal] = useState(false);
  const eventTypeRef = useRef<HTMLDivElement>(null);
  const fieldRefs = {
    emailTemplate: useRef<HTMLInputElement>(null),
    body: useRef<HTMLDivElement>(null)
  };
  const { modalsOpen } = ApplicationState;

  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);

  const { data: emailTemplates = [], isLoading: loadingEmailTemplates } = useEmailTemplatesOptions({
    onError: (message: string) => displayError(message)
  });

  useGetEmailTemplate({ selectedEmailTemplate, setBody, dispatch, setSubject });

  const { placeholders } = useGetSmsTemplatePlaceholders({ kind: 'invite', dispatch });

  const handleClose = () => dispatch({ type: 'SET_MODALS_OPEN', payload: null });
  const { displayError, displaySuccess } = useDispatch(dispatch);

  const getTimeslots = useCallback(async (eventId?: number) => {
    setIsLoading((prev) => ({ ...prev, events: true }));
    const date = new Date().toISOString();
    const queryString = `/api/v4/calendar/events?${
      eventId ? 'event_type_id=' + eventId : ''
    }&date=${date}&override_limit=true`;
    try {
      const response = await fetch(queryString, { method: 'GET' });
      const data = await response.json();
      setAllEvents(data.events || []);
    } catch (error) {
      displayError('Failed to load timeslots');
    } finally {
      setIsLoading((prev) => ({ ...prev, events: false }));
    }
  }, []);

  const { data: recruiters, isLoading: loadingRecruiters } = useQuery({
    queryKey: ['recruiters'],
    queryFn: async () => {
      const { res } = await API.getRecruiters();
      return res.users;
    },
    onError: () => displayError('Failed to fetch recruiters data.')
  });

  const { data: jobs, isLoading: loadingJobs } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const { res } = await API.getEventJobs();
      return res.jobs;
    }
  });

  const jobOptions = jobs?.map((job: IJob) => `${job.id} - ${job.reference} - ${job.title}`);

  const { data: eventTypes, isLoading: loadingEventTypes } = useQuery({
    queryKey: ['event types'],
    queryFn: async () => {
      const { res } = await API.getEventTypes();
      return res.event_types;
    },
    onError: () => displayError('Failed to fetch event types data.')
  });

  const { mutate: sendEmail, isLoading: sendingEmail } = useMutation({
    mutationFn: async () => {
      if (!selectedEventType || !application || !selectedEmailTemplate) return;
      const res = await API.inviteByEmail({
        user_id: selectedRecruiter?.id,
        event_type_id: selectedEventType.id,
        job_id: application.job.id,
        invitation: {
          application_ids: [application.id],
          subject: subject,
          body: body,
          email_template_id: selectedEmailTemplate.split(' -')[0]
        }
      });
      return res;
    },
    onSuccess: () => {
      displaySuccess('Invite to timeslot email notification has been sent successfully');
      handleClose();
    },
    onError: () => displayError('Failed to send invite to timeslot email')
  });

  const handleSelectTemplate = async (template: string) => {
    if (!template) {
      setSelectedEmailTemplate(null);
      setBody('');
      return;
    }
    setSelectedEmailTemplate(template);
    const templateId = template.split(' -')[0];
    try {
      const response = await fetch(`/api/v4/calendar/email_templates/${templateId}`, {
        method: 'GET'
      });
      const data = await response.json();
      setSubject(data.subject);
      setBody(data.body);
      if (response.status !== 200) {
        throw new Error(data);
      }
    } catch (error) {
      displayError('Error loading template');
      setBody('');
    }
  };

  const { mutate: sendSms, isLoading: sendingSms } = useMutation({
    mutationFn: async () => {
      if (!selectedEventType || !application || !smsMessage) return;
      const { res } = await API.sendInviteSms(
        application.job.id,
        selectedEventType.id,
        [application.id],
        smsMessage,
        smsTemplate?.id
      );
      const smsResponse = res as SMSResponse;
      if (smsResponse.success !== 0) {
        displaySuccess('Invite to timeslot SMS notification has been sent successfully');
        handleClose();
      } else {
        displayError('There is an error in sending SMS notification');
      }
      return smsResponse;
    }
  });

  const buttonName = step === 1 ? 'Next' : 'Send';

  const validateInput = () => {
    const fieldsErrors: Record<string, string> = {};
    if (step === 1) {
      !filterType && (fieldsErrors.recruiter = 'Must select a recruiter');
      !selectedEventType && (fieldsErrors.eventType = 'Must select an event type');
    } else {
      if (notification === 'email') {
        !selectedEmailTemplate && (fieldsErrors.template = 'Must select a template');
        !body.trim() && (fieldsErrors.body = 'Must add email body');
      } else {
        !smsMessage.trim() && (fieldsErrors.sms = 'Must add SMS content');
      }
    }
    setErrors(fieldsErrors);
    if (fieldsErrors.template) {
      scrollToElement(fieldRefs.emailTemplate);
    } else if (fieldsErrors.body) {
      scrollToElement(fieldRefs.body);
    }
    return !Object.keys(fieldsErrors).length;
  };

  const handleEmailSubmission = () => {
    const missingPlaceholders = checkMissingPlaceholders(
      [INVITE_TO_TIMESLOT_PLACEHOLDER],
      body.trim()
    );
    validateInput() &&
      (missingPlaceholders ? setMissingPlaceholderConfirmation(true) : sendEmail());
  };
  const handleSubmit = () => {
    if (step === 1) {
      if (events.length === 0) {
        setNoTimeslots(true);
        setNotEnoughSpots(false);
        setShowNoTimeslotsModal(true);
        return;
      }
      validateInput() && setStep(2);
    } else {
      notification === 'email' ? handleEmailSubmission() : validateInput() && sendSms();
    }
  };

  useEffect(() => {
    if (modalsOpen === ModalType.INVITE_TO_TIMESLOT) {
      getTimeslots();
    }
  }, [modalsOpen, ScoutCalendarState.showCreateEventModal]);

  useEffect(() => {
    let filtered = allEvents;

    if (selectedEventType) {
      filtered = filtered.filter((event) => event.event_type?.id === selectedEventType.id);
    }

    if (filterType === 'recruiter' && selectedRecruiter) {
      filtered = filtered.filter((event) =>
        event.attendees?.some(
          (attendee) => attendee.type === 'User' && attendee.id === selectedRecruiter.id
        )
      );
    } else if (filterType === 'job' && selectedJob) {
      const jobId = Number(selectedJob.split(' -')[0]);
      filtered = filtered.filter((event) => {
        if (Array.isArray(event.jobs)) {
          return event.jobs.some((job) => job.id === jobId);
        }
        if (event.jobs && typeof event.jobs === 'object') {
          return event.jobs.id === jobId;
        }
        return false;
      });
    } else if (filterType === 'owner' && selectedRecruiter) {
      filtered = filtered.filter((event) => event.owner_id === selectedRecruiter.id);
    }

    setEvents(filtered);
    setSpots(filtered.reduce((sum, event) => sum + (event.available_spots || 0), 0));
  }, [selectedEventType, filterType, selectedRecruiter, selectedJob, allEvents]);

  return (
    <>
      <StyledModal
        isOpen={modalsOpen === ModalType.INVITE_TO_TIMESLOT}
        label="Invite to timeslot modal"
        handleClose={handleClose}
        styleOverrides={{ ...styles.modalStyleOverrides, width: '900px' }}
        disableEnforceFocus
      >
        <Stack sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
          <Stack
            id="add-to-event-title"
            sx={{
              ...classes.actionModalHeader,
              paddingBottom: 3,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Stack>Invite to timeslot</Stack>
            {step === 1 && (
              <CreateEventButton
                dispatch={calendarDispatch}
                sx={classes.createEventButton}
                id="create-event-button"
              />
            )}
          </Stack>
          <Box
            sx={{ padding: '0px 8px', flexGrow: 1, overflow: 'auto' }}
            id="invite-to-event-modal-content"
            aria-label="Invite to event modal content"
          >
            <Box role="form" id="invite-to-event-form" aria-label="Invite to event form">
              {step === 1 && (
                <InviteToTimeslotStep1
                  eventTypes={eventTypes || []}
                  selectedEventType={selectedEventType}
                  setSelectedEventType={setSelectedEventType}
                  errors={errors}
                  setErrors={setErrors}
                  filterType={filterType}
                  setFilterType={setFilterType}
                  selectedRecruiter={selectedRecruiter}
                  setSelectedRecruiter={setSelectedRecruiter}
                  selectedJob={selectedJob}
                  setSelectedJob={setSelectedJob}
                  recruiters={recruiters || []}
                  loadingRecruiters={loadingRecruiters}
                  jobOptions={jobOptions}
                  loadingJobs={loadingJobs}
                  allEvents={allEvents}
                  events={events}
                  isLoading={isLoading}
                  spots={spots}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                  timeslotHovered={timeslotHovered}
                  setTimeslotHovered={setTimeslotHovered}
                  eventTypeRef={eventTypeRef}
                  isApplicationPage={true}
                />
              )}
              {step === 2 && (
                <InviteToTimeslotStep2
                  notification={notification}
                  setNotification={setNotification}
                  selectedTemplate={selectedEmailTemplate}
                  setSelectedTemplate={setSelectedEmailTemplate}
                  emailTemplates={emailTemplates}
                  loadingEmailTemplates={loadingEmailTemplates}
                  handleSelectTemplate={handleSelectTemplate}
                  errors={errors}
                  setErrors={setErrors}
                  body={body}
                  setBody={setBody}
                  sendableBody={body}
                  setSendableBody={setBody}
                  smsMessage={smsMessage}
                  setSmsMessage={setSmsMessage}
                  smsTemplate={smsTemplate}
                  setSmsTemplate={setSmsTemplate}
                  excludeApplicants={excludeApplicants}
                  setExcludeApplicants={setExcludeApplicants}
                  placeholders={placeholders}
                  templateRef={fieldRefs.emailTemplate}
                  bodyRef={fieldRefs.body}
                  smsTemplatePermissions={smsTemplatePermissions}
                  setSnackbar={({ message, state }: { message: string; state: string }) => {
                    if (state === 'error') {
                      displayError(message);
                    } else {
                      displaySuccess(message);
                    }
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ ...classes.modalFooter, columnGap: 1, paddingTop: 2 }}>
            <Button
              id="close-invite-to-timeslot-modal-button"
              sx={classes.modalCancelButton}
              onClick={handleClose}
            >
              Cancel
            </Button>
            {step !== 1 && (
              <Button
                onClick={() => {
                  setStep(1);
                  setErrors({});
                }}
                sx={{ ...sharedClasses.genericButtonSecondary, minWidth: 'unset', padding: 1 }}
              >
                <NavigateBeforeIcon />
              </Button>
            )}
            <Button
              id="invite-to-timeslot-submit-button"
              type="submit"
              sx={{ ...sharedClasses.genericButton, minWidth: '75px' }}
              disabled={loadingJobs || loadingRecruiters || loadingEventTypes || isLoading.events}
              onClick={handleSubmit}
            >
              {sendingSms || sendingEmail ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                buttonName
              )}
            </Button>
          </Box>
        </Stack>
        {missingPlaceholderConfirmation && (
          <MissingPlaceholdersConfirmationModal
            missingPlaceholderList={INVITE_TO_TIMESLOT_PLACEHOLDER}
            overrideFunction={sendEmail}
            missingPlaceholderConfirmation={missingPlaceholderConfirmation}
            setMissingPlaceholderConfirmation={setMissingPlaceholderConfirmation}
            overridingLoading={sendingEmail}
          />
        )}
      </StyledModal>
      <NoTimeslotsDialog
        open={showNoTimeslotsModal}
        noTimeslots={noTimeslots}
        notEnoughSpots={notEnoughSpots}
        onClose={() => setShowNoTimeslotsModal(false)}
        onContinue={() => {
          setNoTimeslots(false);
          setNotEnoughSpots(false);
          setShowNoTimeslotsModal(false);
          setStep(2);
        }}
      />
    </>
  );
}
